import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { useLocation } from 'react-router-dom';

import Skeleton from '@atlaskit/skeleton';
import MarketplaceIcon from '@atlaskit/icon/core/marketplace';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { MenuList } from '@atlaskit/navigation-system/side-nav-items/menu-list';
import { LinkMenuItem } from '@atlaskit/navigation-system/side-nav-items/link-menu-item';
import {
	MenuSection,
	MenuSectionHeading,
} from '@atlaskit/navigation-system/side-nav-items/menu-section';

import { isFedRamp } from '@atlassian/atl-context';

import type { useWebItemLocationWebItemType as WebItem } from '@confluence/web-item-location/entry-points/useWebItemLocation.ts';
import { GLOBAL_PAGE_FORGE, SITE_ANALYTICS } from '@confluence/named-routes/entry-points/routes.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import type { ForgeUIGlobalPageExtensionType } from '@confluence/forge-ui/entry-points/types.ts';
import { extensionTitle } from '@confluence/forge-ui/entry-points/extensionTitle.ts';
import { fg } from '@confluence/feature-gating';
import { LoadableAfterPaint } from '@confluence/loadable';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import { ANALYTICS_KEY, ANALYTICS_KEY_MODERATE } from './appKeys.tsx';
import { AppIcon } from './AppIcon.tsx';

const AsyncDiscoverSection = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-apps-menu-recommeded" */
				'./DiscoverSection.tsx'
			)
		).DiscoverSection,
});

const DISCOVER_URL = `${CONTEXT_PATH}/marketplace`;

type AppsMenuContentProps = {
	loading: boolean;
	webItems: WebItem[];
	extensions: ForgeUIGlobalPageExtensionType[];
};

export const AppsMenuContent = ({ loading, webItems, extensions }: AppsMenuContentProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireAppsNavigationItemClicked = (itemId?: string | null) => () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'appsNavigationMenuItem',
				source: 'globalNavigation',
				attributes: {
					navigationLayer: 'global',
					navigationContainer: 'sidebar',
					navVersion: '4',
					componentType: 'customItem',
					itemType: 'app',
					itemId,
					section: 'yourApps',
				},
			},
		}).fire();
	};

	const location = useLocation();

	const hasApps = webItems.length > 0 || extensions.length > 0;
	const displayApps = hasApps && !loading;

	return (
		<>
			<MenuSection>
				{displayApps && (
					<MenuSectionHeading>
						<FormattedMessage {...i18n.yourApps} />
					</MenuSectionHeading>
				)}
				<MenuList>
					{displayApps && (
						<AppItems
							webItems={webItems}
							extensions={extensions}
							pathname={location?.pathname || ''}
							fireAppsNavigationItemClicked={fireAppsNavigationItemClicked}
						/>
					)}
					{loading && <LoadingSkeletons />}
				</MenuList>
			</MenuSection>
			{fg('confluence_nav_4_apps_menu_recommend') && <AsyncDiscoverSection />}
			{(fg('use_frontend_feature_encoding_sdk')
				? isFeatureEnabled('adminhub', 'adminhub-apps-discovery')
				: !isFedRamp()) && (
				<MenuSection>
					<MenuList>
						<LinkMenuItem
							isSelected={location?.pathname?.includes(DISCOVER_URL)}
							href={DISCOVER_URL}
							elemBefore={<MarketplaceIcon label="" color="currentColor" />}
							onClick={fireAppsNavigationItemClicked('exploreApps')}
						>
							{loading || hasApps ? (
								<FormattedMessage {...i18n.exploreMoreApps} />
							) : (
								<FormattedMessage {...i18n.exploreApps} />
							)}
						</LinkMenuItem>
					</MenuList>
				</MenuSection>
			)}
		</>
	);
};

type AppItemsProps = {
	webItems: WebItem[];
	extensions: ForgeUIGlobalPageExtensionType[];
	pathname: string;
	fireAppsNavigationItemClicked: (itemId?: string | null) => () => void;
};

const AppItems = ({
	webItems,
	extensions,
	pathname,
	fireAppsNavigationItemClicked,
}: AppItemsProps) => (
	<>
		{webItems.map(({ id, url, label, icon, completeKey, styleClass }) => {
			const href = fixWebItemUrl(url, completeKey);

			/**
			 * Injecting the `styleClass` using a callback ref.
			 *
			 * This could be a `useLayoutEffect` too, but we'd need to refactor this
			 * render prop function to be a component.
			 *
			 * A `useLayoutEffect` might be desirable if we need to worry about
			 * removing the styles, such as if `styleClass` can change.
			 *
			 * From what I can tell `styleClass` cannot change so this doesn't matter.
			 *
			 * Also, a callback ref should be called before any effects (better for TTVC?)
			 */
			const injectStyleClass = (element: HTMLElement | null) => {
				if (element === null) {
					return;
				}

				if (typeof styleClass !== 'string' || styleClass.length === 0) {
					return;
				}

				const classList = styleClass.split(' ');

				element.classList.add(...classList);
			};

			return (
				<LinkMenuItem
					key={id}
					ref={injectStyleClass}
					href={href}
					elemBefore={<AppIcon url={icon?.path} completeKey={completeKey} />}
					isSelected={getIsSelected(pathname, href)}
					onClick={fireAppsNavigationItemClicked(completeKey)}
				>
					{label}
				</LinkMenuItem>
			);
		})}
		{extensions.map((extension) => {
			const href = GLOBAL_PAGE_FORGE.toUrl({
				appId: extension.appId,
				envId: extension.environmentId,
				forgeManifestRoute: extension.properties.route,
			});
			return (
				<LinkMenuItem
					key={extension.key}
					href={href}
					isSelected={getIsSelected(pathname, href)}
					elemBefore={<AppIcon url={extension.properties.icon} />}
				>
					{extensionTitle(extension)}
				</LinkMenuItem>
			);
		})}
	</>
);

const LoadingSkeletons = () => (
	<Box paddingBlockStart="space.100" testId="loading">
		<Stack space="space.100">
			<Skeleton width="100%" height="20px" borderRadius="3px" />
			<Skeleton width="100%" height="20px" borderRadius="3px" />
			<Skeleton width="100%" height="20px" borderRadius="3px" />
		</Stack>
	</Box>
);

const absoluteUrlCheck = new RegExp('^(?:[a-z]+:)?//', 'i');

const fixWebItemUrl = (url?: string | null, completeKey?: string | null): string => {
	if (!url) {
		return '#';
	}

	if (completeKey === ANALYTICS_KEY || completeKey === ANALYTICS_KEY_MODERATE) {
		return SITE_ANALYTICS.toUrl();
	}

	if (absoluteUrlCheck.test(url)) {
		return url;
	}

	if (url.startsWith(CONTEXT_PATH)) {
		return url;
	}

	return `${CONTEXT_PATH}${url}`;
};

const getIsSelected = (pathname: string, href: string): boolean => {
	const hrefWithoutQuery = href.split('?')[0];
	return pathname === hrefWithoutQuery;
};

const i18n = defineMessages({
	yourApps: {
		id: 'side-navigation.global.apps.your-apps',
		defaultMessage: 'Your apps',
		description: 'Label for the section of the global navigation that displays apps',
	},
	exploreApps: {
		id: 'side-navigation.global.apps.explore-apps',
		defaultMessage: 'Explore apps',
		description:
			'Label for the link to find apps. Appears in global navigation when there are no apps installed yet.',
	},
	exploreMoreApps: {
		id: 'side-navigation.global.apps.explore-more-apps',
		defaultMessage: 'Explore more apps',
		description:
			'Label for the link to find apps. Appears in global navigation, underneath already installed apps.',
	},
});
